import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '@/constants/app'
import { backendLocale, currentLocale, i18n } from '@/i18n'
import { ApiRequestError } from '@/lib/api-errors'
import type { ApiError, ApiResource, AuthTokenPayload } from '@/types/api'

const TOKEN_STORAGE_KEY = 'auth.token'
const REFRESH_TOKEN_STORAGE_KEY = 'auth.refresh_token'
const USER_STORAGE_KEY = 'auth.user'

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

/**
 * No request is allowed to wait forever. A phone that sleeps or drops its connection mid-request
 * leaves it hanging, and every screen that reads "one at a time" then waits on it for good. Uploads
 * (photos, signatures) get far longer because a slow connection really does take that long.
 */
const READ_TIMEOUT_MS = 25_000
const UPLOAD_TIMEOUT_MS = 180_000

export const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: READ_TIMEOUT_MS,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

const refreshHttp = axios.create({
  baseURL: API_BASE_URL,
  timeout: READ_TIMEOUT_MS,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

let refreshPromise: Promise<AuthTokenPayload> | null = null

/**
 * Called once when a signed-in person's session can no longer be used (the access token was
 * refused and it could not be renewed). The app registers it at start-up to clear its state and
 * send the person to their sign-in page; this file cannot do that itself without importing the
 * router, which imports this file.
 */
let sessionExpiredHandler: (() => void) | null = null

export function onSessionExpired(handler: () => void) {
  sessionExpiredHandler = handler
}

/**
 * Requests that prove who you are rather than act as you: signing in, the code check, renewing, and
 * signing out. A 401 on those is a refused attempt, not a lost session, so it must not throw the
 * person out of a page.
 */
function isCredentialRequest(url: string | undefined) {
  return /^\/?auth\/(?!me\b|profile\b)/.test(url ?? '')
}

export function getStoredToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY)
}

export function getStoredRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
}

export function persistToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
  }
}

export function persistRefreshToken(token: string | null) {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token)
  } else {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
  }
}

function persistUser(user: AuthTokenPayload['user'] | null) {
  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
  } else {
    localStorage.removeItem(USER_STORAGE_KEY)
  }
}

function clearStoredSession() {
  persistToken(null)
  persistRefreshToken(null)
  persistUser(null)
}

function persistSession(payload: AuthTokenPayload) {
  persistToken(payload.access_token)
  persistRefreshToken(payload.refresh_token)
  persistUser(payload.user)
}

async function refreshSession() {
  const refreshToken = getStoredRefreshToken()

  if (!refreshToken) {
    throw new Error(i18n.global.t('errors.unauthenticated'))
  }

  if (!refreshPromise) {
    const locale = backendLocale(currentLocale())

    refreshPromise = refreshHttp
      .post<ApiResource<AuthTokenPayload>>(
        '/auth/refresh',
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            'X-Locale': locale,
            'Accept-Language': locale
          }
        }
      )
      .then((response) => {
        persistSession(response.data.data)

        return response.data.data
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Axios's own default is "no timeout", so the client's default is the short one, and an upload asks for the long one.
  if (config.data instanceof FormData && config.timeout === READ_TIMEOUT_MS) config.timeout = UPLOAD_TIMEOUT_MS

  const token = getStoredToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  const locale = backendLocale(currentLocale())
  config.headers['X-Locale'] = locale
  config.headers['Accept-Language'] = locale
  return config
})

type ApiErrorEnvelope = {
  message?: string
  code?: string
  errors?: unknown
  trace_id?: string
  error?: {
    code?: string
    message?: string
    field?: string | null
    trace_id?: string
  }
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorEnvelope>) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      // The token this request was sent with. A late answer for an old session must not wipe a
      // newer one: signing in as someone else while a page of the previous account still had a
      // request in flight logged the new person out a moment after they arrived.
      const sentWith = getStoredToken()
      const sentRefreshWith = getStoredRefreshToken()

      try {
        const payload = await refreshSession()
        originalRequest.headers.Authorization = `Bearer ${payload.access_token}`

        return http(originalRequest)
      } catch {
        if (getStoredToken() === sentWith && getStoredRefreshToken() === sentRefreshWith) {
          clearStoredSession()
          // Only a person who HAD a session loses one: a guest's 401 (public pages, a wrong code)
          // is just an error for the screen to show.
          if (sentWith && !isCredentialRequest(originalRequest.url)) sessionExpiredHandler?.()
        }
      }
    }

    const envelope = error.response?.data
    const normalizedError: ApiError = {
      message: envelope?.error?.message || envelope?.message || error.message || i18n.global.t('errors.requestFailed'),
      status: error.response?.status,
      code: envelope?.error?.code || envelope?.code,
      field: envelope?.error?.field ?? null,
      traceId: envelope?.error?.trace_id || envelope?.trace_id,
      details: envelope?.errors ?? envelope
    }

    return Promise.reject(new ApiRequestError(normalizedError))
  }
)
